import Builder from 'react-native-jala-orm/src/model/Builder';
import _ from 'lodash';
import Expression from 'react-native-jala-orm/src/query/Expression';
import moment from 'moment';
import SyncableModel from './SyncableModel';
import Cycle from './Cycle';
import Farm from './Farm';
import Pond from './Pond';

export default class FeedingTrayControl extends SyncableModel {
    static name = 'FeedingTrayControl'

    _fillable = [
      'id', 'logged_at', 'feed_logged_at', 'cycle_id', 'tray_number', 'feed_remaining_percent', 'remark',
      'created_at', 'updated_at', 'deleted_at', 'synced_at',
    ]

    static _parents = ['cycle'];

    static endpoint = '/feeding_tray_controls'

    static allowReplaceUpdate = true

    static _boot() {
      super._boot();

      this.addGlobalScope('global', (query: Builder) => {
        if (_.isEmpty(query.getQuery().columns)) {
          query.select([query.qualifyColumn('*')]);
        }

        const cycle = (new Cycle()).setTable(`${query.getModel().getTable()}_cycles`);
        query.leftJoin(
          `${(new Cycle()).getTable()} as ${cycle.getTable()}`,
          cycle.getQualifiedKeyName(), query.qualifyColumn(cycle.getForeignKey())
        );

        const pond = (new Pond()).setTable(`${query.getModel().getTable()}_ponds`);
        query.leftJoin(
          `${(new Pond()).getTable()} as ${pond.getTable()}`,
          pond.getQualifiedKeyName(), cycle.qualifyColumn(pond.getForeignKey())
        );

        const farm = (new Farm()).setTable(`${query.getModel().getTable()}_farms`);
        query.leftJoin(
          `${(new Farm()).getTable()} as ${farm.getTable()}`,
          farm.getQualifiedKeyName(), pond.qualifyColumn(farm.getForeignKey())
        );
      });
    }

    qualifyColumn(column: string): string {
      if (column === '*') {
        return new Expression(
          `${this.getTable()}.*, `
            + `${this.qualifyColumn('cycle_id_feed_logged_at')} as cycle_id_feed_logged_at, `
            + `${this.qualifyColumn('feed_logged_date')} as feed_logged_date, `
            + `${this.qualifyColumn('feed_logged_time')} as feed_logged_time, `
            + `${this.qualifyColumn('age')} as age`
        );
      }
      if (column === 'cycle_id_feed_logged_at') {
        return new Expression(`DATE(${this.qualifyColumn('feed_logged_at')}, ${this.qualifyColumn('timezone')})`);
      }
      if (column === 'feed_logged_date') {
        return new Expression(`DATE(${this.qualifyColumn('feed_logged_at')})`);
      }
      if (column === 'feed_logged_time') {
        return new Expression(`TIME(${this.qualifyColumn('feed_logged_at')})`);
      }
      if (column === 'age') {
        const cycle = (new Cycle()).setTable(`${this.getTable()}_cycles`);
        return new Expression(
          '('
            + 'CAST('
              + `(JULIANDAY(${this.qualifyColumn('cycle_id_feed_logged_at')}) - `
              + `JULIANDAY(${cycle.qualifyColumn('started_at')})) AS INTEGER`
            + `) + COALESCE(${cycle.qualifyColumn('initial_age')}, 0)`
          + ')'
        );
      }
      if (column === 'timezone') {
        const timezone = moment().format('Z');
        const farm = (new Farm()).setTable(`${this.getTable()}_farms`);
        return new Expression(`COALESCE(${farm.qualifyColumn('timezone')}, '${timezone}')`);
      }
      return super.qualifyColumn(column);
    }

    /**
     * @param {String} value
     * @return {String}
     */
    getLoggedDateAttribute(value) {
      if (value) return value;
      if (this.exists) {
        this._attributes.logged_date = this._attributes.logged_at ? moment.utc(this._attributes.logged_at).format('YYYY-MM-DD') : null;
        this.syncOriginalAttribute('logged_date');
        return this._attributes.logged_date;
      }

      return this._attributes.logged_at ? moment.utc(this._attributes.logged_at).format('YYYY-MM-DD') : null;
    }

    /**
     * @param {String} value
     */
    setLoggedDateAttribute(value) {
      const date = moment.utc(value || undefined, 'YYYY-MM-DD', false);
      this._attributes = {
        ...this._attributes,
        logged_at: date.format('YYYY-MM-DD') + moment.utc(this._attributes.logged_at || undefined).format(' HH:mm:ss')
      };
      if (this.exists) {
        this._attributes = {
          ...this._attributes,
          logged_date: date.format('YYYY-MM-DD')
        };
        this.syncOriginalAttribute('logged_date');
      }
      return this;
    }

    /**
     * @param {String} value
     * @return {String}
     */
    getLoggedTimeAttribute(value) {
      if (value) return value;
      if (this.exists) {
        this._attributes.logged_time = this._attributes.logged_at ? moment.utc(this._attributes.logged_at).format('HH:mm:ss') : null;
        this.syncOriginalAttribute('logged_time');
        return this._attributes.logged_time;
      }

      return this._attributes.logged_at ? moment.utc(this._attributes.logged_at).format('HH:mm:ss') : null;
    }

    /**
     * @param {String} value
     */
    setLoggedTimeAttribute(value) {
      const time = moment.utc(value || undefined, 'HH:mm:ss', false);
      this._attributes = {
        ...this._attributes,
        logged_at: moment.utc(this._attributes.logged_at || undefined).format('YYYY-MM-DD') + time.format(' HH:mm:ss')
      };
      if (this.exists) {
        this._attributes = {
          ...this._attributes,
          logged_time: time.format('HH:mm:ss')
        };
        this.syncOriginalAttribute('logged_time');
      }
      return this;
    }

    relationCycle() {
      return Cycle.query().withoutGlobalScope('soft_delete').whereKey(this.cycle_id);
    }
}
